import { useEffect, useState } from 'react';

/**
 * Lógica de convite de instalação do PWA — COMPARTILHADA entre o app da
 * plataforma (B2B, components/pwa/PwaPrompts.tsx) e o app do comprador
 * (white-label, components/comprador/InstalarAppBanner.tsx).
 *
 * PROATIVO (correção): o convite aparece por conta própria no mobile — NÃO
 * espera o `beforeinstallprompt`. Esse evento é disparado pelo Chrome só quando
 * ELE quer (heurística de engajamento + SW controlando a página), então prender
 * o banner a ele o deixava invisível em produção no Android. Agora:
 *  - mobile, não instalado, não resolvido → mostra o convite já;
 *  - se o `beforeinstallprompt` for capturado, o botão instala NATIVAMENTE;
 *  - se não houver evento (iOS, ou Chrome que ainda não disparou), o botão cai
 *    numa instrução de como instalar pelo menu do navegador.
 *
 * Regras de memória (inalteradas):
 *  - Nunca aparece se já instalado (display-mode standalone / iOS navigator.standalone).
 *  - `appinstalled` → resolvido PERMANENTE (some para sempre).
 *  - Dispensar (X) OU ser exibido → grava a flag persistente no localStorage;
 *    aparece NO MÁXIMO UMA VEZ por dispositivo até ser decidido.
 *  - FOCO MOBILE: no desktop nunca aparece.
 * A chave de dispensa é PARAMETRIZADA (B2B fixa; comprador por slug).
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function motivoResolvido(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function marcarResolvido(key: string, motivo: string): void {
  try {
    localStorage.setItem(key, motivo);
  } catch {
    /* localStorage indisponível (modo privado/quota) — no pior caso o convite
       pode reaparecer, mas nunca quebra a app. */
  }
}

export function ehStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
export function ehMobile(): boolean {
  const ua = navigator.userAgent;
  return /android|iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
export function ehIOS(): boolean {
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
export function ehIOSSafari(): boolean {
  const safari = /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios|chrome|android/i.test(navigator.userAgent);
  return ehIOS() && safari;
}

export type ModoInstalar = 'android' | 'ios' | null;

/**
 * Hook do convite. `modo`: 'ios' (instrução Compartilhar → Adicionar), 'android'
 * (botão Instalar) ou null (desktop / já instalado / já resolvido).
 * `instalarNativo`: função de instalação nativa, presente SÓ quando o Chrome já
 * ofereceu o `beforeinstallprompt`; null caso contrário (o componente então
 * mostra a instrução manual). `dispensar`: fecha e grava a flag.
 */
export function usePwaInstalar(
  dismissKey: string,
  opts?: {
    /**
     * PERSISTENTE: o convite volta a aparecer a cada sessão enquanto o app não
     * for instalado, SÓ some ao ser DISPENSADO (X) ou instalado. Sem isso
     * (default), aparece no máximo 1x por dispositivo (marca resolvido ao
     * exibir). Usado pelo CTA proativo do comprador (bloco de destaque).
     */
    persistente?: boolean;
  },
): {
  modo: ModoInstalar;
  instalarNativo: (() => Promise<void>) | null;
  dispensar: () => void;
} {
  const [evento, setEvento] = useState<BeforeInstallPromptEvent | null>(null);
  const [oculto, setOculto] = useState(true);

  useEffect(() => {
    if (ehStandalone()) {
      marcarResolvido(dismissKey, 'standalone');
      return;
    }
    if (!ehMobile()) return;
    // No modo persistente, só "dispensado"/"appinstalled"/"standalone" bloqueiam —
    // o "oferecido" antigo (do banner one-shot anterior) é IGNORADO, senão quem
    // já tinha aberto o app nunca mais veria o CTA. No modo padrão, qualquer
    // marca resolve (1x/dispositivo).
    const marca = motivoResolvido(dismissKey);
    const bloqueado = opts?.persistente
      ? marca === 'dispensado' || marca === 'appinstalled' || marca === 'standalone'
      : marca != null;
    if (bloqueado) return;
    // PROATIVO. No padrão, marca "resolvido" ao exibir; no persistente, NÃO marca.
    if (!opts?.persistente) marcarResolvido(dismissKey, 'oferecido');
    setOculto(false);
  }, [dismissKey]);

  // App instalado durante o uso → some para sempre.
  useEffect(() => {
    const onInstalled = () => {
      marcarResolvido(dismissKey, 'appinstalled');
      setEvento(null);
      setOculto(true);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, [dismissKey]);

  // Captura o evento nativo se/quando o Chrome disparar — para o botão instalar
  // de verdade (não é PRÉ-requisito para mostrar o convite).
  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvento(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const modo: ModoInstalar = oculto ? null : ehIOSSafari() ? 'ios' : 'android';

  const instalarNativo = evento
    ? async () => {
        const e = evento;
        setEvento(null);
        marcarResolvido(dismissKey, 'instalar-clicado');
        await e.prompt();
        await e.userChoice.catch(() => undefined);
      }
    : null;

  function dispensar() {
    marcarResolvido(dismissKey, 'dispensado');
    setEvento(null);
    setOculto(true);
  }

  return { modo, instalarNativo, dispensar };
}

/**
 * Convite PERMANENTE de instalação (ícone fixo no header). Diferente do banner:
 * NÃO tem memória de dispensa — fica sempre visível no mobile enquanto o app
 * não está instalado (independe de banner dispensado e de beforeinstallprompt).
 * Some só em standalone (já instalado) ou ao disparar `appinstalled`; no desktop
 * nunca aparece. `plataforma` decide a instrução (iOS x Android) quando não há
 * instalador nativo.
 */
export function useInstalarPermanente(): {
  visivel: boolean;
  plataforma: 'ios' | 'android';
  instalarNativo: (() => Promise<void>) | null;
} {
  const [visivel, setVisivel] = useState(false);
  const [evento, setEvento] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    setVisivel(ehMobile() && !ehStandalone());
    const onInstalled = () => {
      setVisivel(false);
      setEvento(null);
    };
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvento(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('beforeinstallprompt', onPrompt);
    };
  }, []);

  const plataforma: 'ios' | 'android' = typeof navigator !== 'undefined' && ehIOS() ? 'ios' : 'android';
  const instalarNativo = evento
    ? async () => {
        const e = evento;
        setEvento(null);
        await e.prompt();
        await e.userChoice.catch(() => undefined);
      }
    : null;

  return { visivel, plataforma, instalarNativo };
}
